import { supabase } from '../lib/supabase';

// ... Add this helper function ... //
const toCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    name: obj?.name,
    contactEmail: obj?.contact_email,
    contactPhone: obj?.contact_phone,
    address: obj?.address,
    websiteUrl: obj?.website_url,
    rating: obj?.rating,
    logoUrl: obj?.logo_url,
    logoPath: obj?.logo_path,
    createdAt: obj?.created_at,
    updatedAt: obj?.updated_at
  };
};

export const supplierService = {
  async getAll() {
    try {
      console.log('📡 supplierService.getAll called');
      
      const { data, error } = await supabase?.from('suppliers')?.select('*')?.order('name');
      
      if (error) {
        console.error('❌ supplierService.getAll error:', error);
        throw error;
      }
      
      const result = data?.map(toCamelCase) || [];
      console.log(`✅ supplierService.getAll success: ${result?.length} suppliers fetched`);
      
      return result;
    } catch (error) {
      console.error('❌ supplierService.getAll caught error:', error);
      throw error;
    }
  },

  async getById(id) {
    const { data, error } = await supabase?.from('suppliers')?.select('*')?.eq('id', id)?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      name: data?.name,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      address: data?.address,
      websiteUrl: data?.website_url,
      rating: data?.rating,
      logoUrl: data?.logo_url,
      logoPath: data?.logo_path,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async create(supplier) {
    const { data, error } = await supabase?.from('suppliers')?.insert({
        name: supplier?.name,
        contact_email: supplier?.contactEmail || null,
        contact_phone: supplier?.contactPhone || null,
        address: supplier?.address || null,
        website_url: supplier?.websiteUrl || null,
        rating: supplier?.rating || 0,
        logo_url: supplier?.logoUrl || null,
        logo_path: supplier?.logoPath || null
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      name: data?.name,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      address: data?.address,
      websiteUrl: data?.website_url,
      rating: data?.rating,
      logoUrl: data?.logo_url,
      logoPath: data?.logo_path,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async update(id, supplier) {
    try {
      console.log('📡 supplierService.update called for ID:', id);
      console.log('📝 Update payload received:', supplier);
      
      // CRITICAL FIX: Fetch current supplier to preserve logo fields
      const { data: currentSupplier, error: fetchError } = await supabase
        ?.from('suppliers')
        ?.select('logo_url, logo_path')
        ?.eq('id', id)
        ?.single();

      if (fetchError) {
        console.error('❌ Error fetching current supplier:', fetchError);
        throw fetchError;
      }

      console.log('📸 Current logo values:', {
        logo_url: currentSupplier?.logo_url,
        logo_path: currentSupplier?.logo_path
      });

      // Build update payload with PRESERVED logo values
      const updateData = {
        name: supplier?.name,
        contact_email: supplier?.contactEmail || null,
        contact_phone: supplier?.contactPhone || null,
        address: supplier?.address || null,
        website_url: supplier?.websiteUrl || null,
        rating: supplier?.rating || 0,
        // CRITICAL: Include existing logo values to prevent NULL overwrites
        logo_url: currentSupplier?.logo_url,
        logo_path: currentSupplier?.logo_path
      };

      console.log('📤 Final update payload:', updateData);

      const { data, error } = await supabase
        ?.from('suppliers')
        ?.update(updateData)
        ?.eq('id', id)
        ?.select()
        ?.single();
      
      if (error) {
        console.error('❌ Update error:', error);
        throw error;
      }

      console.log('✅ Update successful:', data);
      
      return {
        id: data?.id,
        name: data?.name,
        contactEmail: data?.contact_email,
        contactPhone: data?.contact_phone,
        address: data?.address,
        websiteUrl: data?.website_url,
        rating: data?.rating,
        logoUrl: data?.logo_url,
        logoPath: data?.logo_path,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('❌ supplierService.update caught error:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      console.log('📡 supplierService.delete called for ID:', id);
      
      // CRITICAL FIX: Fetch supplier first to get logo_path before deletion
      const { data: supplier, error: fetchError } = await supabase
        ?.from('suppliers')
        ?.select('logo_path')
        ?.eq('id', id)
        ?.single();

      if (fetchError) {
        console.error('❌ Error fetching supplier for deletion:', fetchError);
        throw fetchError;
      }

      console.log('📸 Supplier logo_path to delete:', supplier?.logo_path);

      // Delete logo file from storage if it exists
      if (supplier?.logo_path) {
        console.log('🗑️ Deleting logo file from storage:', supplier?.logo_path);
        const { error: storageError } = await supabase?.storage
          ?.from('company-logos')
          ?.remove([supplier?.logo_path]);

        if (storageError) {
          console.error('⚠️ Error deleting logo from storage:', storageError);
          // Continue with supplier deletion even if logo deletion fails
        } else {
          console.log('✅ Logo file deleted successfully');
        }
      }

      // Delete supplier record from database
      const { error: deleteError } = await supabase
        ?.from('suppliers')
        ?.delete()
        ?.eq('id', id);
      
      if (deleteError) {
        console.error('❌ Error deleting supplier:', deleteError);
        throw deleteError;
      }

      console.log('✅ Supplier deleted successfully');
    } catch (error) {
      console.error('❌ supplierService.delete caught error:', error);
      throw error;
    }
  },

  async uploadLogo(supplierId, file) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `supplier-${supplierId}-${Date.now()}.${fileExt}`;
      const filePath = `suppliers/${fileName}`;

      // Upload file to storage
      const { error: uploadError } = await supabase?.storage
        ?.from('company-logos')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase?.storage
        ?.from('company-logos')
        ?.getPublicUrl(filePath);

      const logoUrl = data?.publicUrl;

      // Update supplier with logo_url, logo_path AND updated_at to trigger re-render
      const { error: updateError } = await supabase
        ?.from('suppliers')
        ?.update({ 
          logo_url: logoUrl,
          logo_path: filePath,
          updated_at: new Date()?.toISOString() // CRITICAL: Force timestamp update
        })
        ?.eq('id', supplierId);

      if (updateError) throw updateError;

      return logoUrl;
    } catch (error) {
      console.error('Logo upload error:', error);
      throw new Error(error?.message || 'Failed to upload logo');
    }
  },

  async deleteLogo(supplierId, logoPath) {
    try {
      // Delete from storage if logoPath exists
      if (logoPath) {
        const { error: deleteError } = await supabase?.storage
          ?.from('company-logos')
          ?.remove([logoPath]);

        if (deleteError) throw deleteError;
      }

      // Update supplier to remove both logo_url and logo_path
      const { error: updateError } = await supabase
        ?.from('suppliers')
        ?.update({ 
          logo_url: null,
          logo_path: null 
        })
        ?.eq('id', supplierId);

      if (updateError) throw updateError;
    } catch (error) {
      console.error('Logo deletion error:', error);
      throw new Error(error?.message || 'Failed to delete logo');
    }
  },

  getLogoUrl(logoPath) {
    if (!logoPath) return null;
    
    const { data } = supabase?.storage
      ?.from('company-logos')
      ?.getPublicUrl(logoPath);
    
    // Add cache-busting parameter to force fresh image load
    const timestamp = new Date()?.getTime();
    return data?.publicUrl ? `${data?.publicUrl}?t=${timestamp}` : null;
  }
};